import type { AnimalItem } from "../mocks/animals";
import type { CustomerItem } from "../mocks/customers";
import type { PaymentRecord } from "../mocks/payments";
import { getAnimalSalesType, getDisplayKupeNo, getDisplaySiparisNo } from "./animalLabels";
import type { ReportPrintSettings } from "./reportPrintSettings";
import { getReportPrintSettings } from "./reportPrintSettings";
import { getShareTotal, isShareOwnedByCustomer } from "./cascadeOps";

export interface SalePrintPayload {
  tarih: string;
  fisNo?: string;
  kupeNo: string;
  siparisNo: string;
  customerName: string;
  saleType: string;
  animalType?: string;
  hisseLabel: string;
  saleAmount: number;
  kesimUcreti: number;
  parcalamaUcreti: number;
  tasimaUcreti: number;
  extraServicesTotal: number;
  grandTotal: number;
  depositAmount?: number;
  remainingAmount?: number;
  operationalNote?: string;
}

export interface PaymentPrintPayload {
  tarih: string;
  fisNo?: string;
  kupeNo: string;
  siparisNo: string;
  customerName: string;
  hisseLabel: string;
  totalSale: number;
  previousPaid: number;
  paymentAmount: number;
  totalPaid: number;
  remainingAmount: number;
  paymentType: string;
  animalType?: string;
  operationalNote?: string;
}

function escapeHtml(value?: string) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function fmtMoney(value: number) {
  return value.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

function fmtDate(value?: string) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("tr-TR");
  } catch {
    return value;
  }
}

function getPageCss(paperSize: ReportPrintSettings["paperSize"]) {
  const pageSize = paperSize === "Tam A4" ? "A4 portrait" : "A5 portrait";
  const maxWidth = paperSize === "Tam A4" ? "190mm" : "138mm";

  return `
    @page { size: ${pageSize}; margin: 9mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #f7f1ea; font-family: Inter, Arial, sans-serif; color: #4f3b2b; }
    body { padding: 0; }
    .page { width: 100%; max-width: ${maxWidth}; margin: 0 auto; }
    .card { background: #fff; border: 1px solid #e6d6c4; border-radius: 20px; overflow: hidden; box-shadow: 0 10px 24px rgba(79,59,43,0.08); }
    .header { padding: 14px 16px; background: linear-gradient(135deg, #6b4c35 0%, #b8865a 100%); color: #fff; }
    .brand { font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; opacity: 0.9; }
    .title { margin-top: 4px; font-size: 22px; font-weight: 800; }
    .subtitle { margin-top: 2px; font-size: 11px; opacity: 0.92; }
    .content { padding: 14px 16px 16px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .info { border: 1px solid #eadfce; border-radius: 14px; background: #fbf7f2; padding: 9px 10px; min-height: 52px; }
    .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.14em; color: #92735b; font-weight: 700; }
    .value { margin-top: 4px; font-size: 13px; font-weight: 700; color: #4f3b2b; word-break: break-word; }
    .value.money { font-size: 15px; }
    .summary { margin-top: 10px; border: 1px solid #eadfce; border-radius: 16px; overflow: hidden; }
    .summary-head { background: #f3eadf; padding: 8px 12px; font-size: 11px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: #6b4c35; }
    .summary-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 9px 12px; border-top: 1px solid #f0e6da; font-size: 12px; }
    .summary-row strong { color: #4f3b2b; }
    .summary-row.total { background: #fff7ee; font-weight: 800; }
    .summary-row.total strong { font-size: 15px; color: #8d5f37; }
    .note-box { margin-top: 10px; padding: 11px 12px; border-radius: 16px; border: 1px solid #e5d1be; background: linear-gradient(135deg, rgba(184,134,90,0.08), rgba(255,255,255,0.95)); }
    .note-title { font-size: 10px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: #8c6a50; }
    .note-text { margin-top: 6px; font-size: 12px; line-height: 1.45; color: #5b4533; white-space: pre-wrap; word-break: break-word; }
    .footer-bubble { margin-top: 10px; border-radius: 999px; padding: 12px 16px; background: #6b4c35; color: #fff; font-size: 11px; line-height: 1.4; text-align: center; }
    .print-table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    .print-table th { background: #f3eadf; color: #6b4c35; text-align: left; padding: 7px 8px; border-bottom: 1px solid #eadfce; }
    .print-table td { padding: 7px 8px; border-top: 1px solid #f0e6da; color: #4f3b2b; vertical-align: top; }
    .muted { color: #92735b; }
  `;
}

function openPrintWindow(title: string, paperSize: ReportPrintSettings["paperSize"], bodyHtml: string) {
  if (typeof window === "undefined") return false;

  const printWindow = window.open("", "_blank", "width=920,height=780");
  if (!printWindow) return false;

  const html = `<!doctype html>
<html lang="tr">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(title)}</title>
<style>${getPageCss(paperSize)}</style>
</head>
<body>
${bodyHtml}
<script>
window.addEventListener('load', function () {
  setTimeout(function () {
    window.focus();
    window.print();
  }, 250);
});
</script>
</body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
}

function renderInfo(label: string, value: string, money = false) {
  return `<div class="info"><div class="label">${escapeHtml(label)}</div><div class="value${money ? " money" : ""}">${escapeHtml(value || "—")}</div></div>`;
}

function renderSummaryRow(label: string, value: string, isTotal = false) {
  return `<div class="summary-row${isTotal ? " total" : ""}"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`;
}

function renderNoteBox(title: string, value?: string) {
  if (!value?.trim()) return "";
  return `<div class="note-box"><div class="note-title">${escapeHtml(title)}</div><div class="note-text">${escapeHtml(value)}</div></div>`;
}

function renderFooterBubble(value?: string) {
  if (!value?.trim()) return "";
  return `<div class="footer-bubble">${escapeHtml(value)}</div>`;
}

export function openSalePrintWindow(
  payload: SalePrintPayload,
  settings = getReportPrintSettings()
) {
  const body = `
  <div class="page">
    <div class="card">
      <div class="header">
        <div class="brand">Evren Çiftliği</div>
        <div class="title">Satış Çıktısı</div>
        <div class="subtitle">Yarım A4 hızlı satış özeti</div>
      </div>
      <div class="content">
        <div class="grid">
          ${renderInfo("Tarih", fmtDate(payload.tarih))}
          ${renderInfo("Fiş No", payload.fisNo || "—")}
          ${renderInfo("Küpe No", payload.kupeNo)}
          ${renderInfo("Sipariş No", payload.siparisNo)}
          ${renderInfo("Müşteri", payload.customerName)}
          ${renderInfo("Hayvan Türü", payload.animalType || "—")}
          ${renderInfo("Satış Tipi", payload.saleType)}
          ${renderInfo("Hisse", payload.hisseLabel)}
          ${renderInfo("Satış Tutarı", fmtMoney(payload.saleAmount), true)}
        </div>

        <div class="summary">
          <div class="summary-head">Hizmet ve Toplam</div>
          ${renderSummaryRow("Kesim", fmtMoney(payload.kesimUcreti))}
          ${renderSummaryRow("Parçalama", fmtMoney(payload.parcalamaUcreti))}
          ${renderSummaryRow("Taşıma", fmtMoney(payload.tasimaUcreti))}
          ${renderSummaryRow("Ek Hizmet Toplamı", fmtMoney(payload.extraServicesTotal))}
          ${renderSummaryRow("Genel Toplam", fmtMoney(payload.grandTotal), true)}
        </div>

        <div class="summary">
          <div class="summary-head">Alınan Ödemeler</div>
          ${renderSummaryRow("Kapora", fmtMoney(payload.depositAmount ?? 0))}
          ${renderSummaryRow("Kalan Tutar", fmtMoney(payload.remainingAmount ?? Math.max(0, payload.grandTotal - (payload.depositAmount ?? 0))), true)}
        </div>

        ${renderNoteBox("Operasyonel Not", payload.operationalNote)}
        ${renderFooterBubble(settings.saleFooterMessage)}
      </div>
    </div>
  </div>`;

  return openPrintWindow("Satış Çıktısı", settings.paperSize, body);
}

export function openPaymentPrintWindow(
  payload: PaymentPrintPayload,
  settings = getReportPrintSettings()
) {
  const body = `
  <div class="page">
    <div class="card">
      <div class="header">
        <div class="brand">Evren Çiftliği</div>
        <div class="title">Ödeme Çıktısı</div>
        <div class="subtitle">Yarım A4 hızlı ödeme özeti</div>
      </div>
      <div class="content">
        <div class="grid">
          ${renderInfo("Tarih", fmtDate(payload.tarih))}
          ${renderInfo("Fiş No", payload.fisNo || "—")}
          ${renderInfo("Küpe No", payload.kupeNo)}
          ${renderInfo("Sipariş No", payload.siparisNo)}
          ${renderInfo("Müşteri", payload.customerName)}
          ${renderInfo("Hayvan Türü", payload.animalType || "—")}
          ${renderInfo("Ödeme Tipi", payload.paymentType)}
          ${renderInfo("Hisse", payload.hisseLabel || "—")}
          ${renderInfo("Bu Ödeme", fmtMoney(payload.paymentAmount), true)}
        </div>

        <div class="summary">
          <div class="summary-head">Ödeme Durumu</div>
          ${renderSummaryRow("Toplam Satış", fmtMoney(payload.totalSale))}
          ${renderSummaryRow("Önceki Ödeme", fmtMoney(payload.previousPaid))}
          ${renderSummaryRow("Toplam Ödenen", fmtMoney(payload.totalPaid))}
          ${renderSummaryRow("Kalan Tutar", fmtMoney(payload.remainingAmount), true)}
        </div>

        ${renderNoteBox("Operasyonel Not", payload.operationalNote)}
        ${renderFooterBubble(settings.paymentFooterMessage)}
      </div>
    </div>
  </div>`;

  return openPrintWindow("Ödeme Çıktısı", settings.paperSize, body);
}

export function buildSalePrintPayload(params: {
  animal: AnimalItem;
  customerName: string;
  hisseLabel: string;
  saleAmount: number;
  kesimUcreti: number;
  parcalamaUcreti: number;
  tasimaUcreti: number;
  grandTotal: number;
  depositAmount?: number;
  remainingAmount?: number;
  operationalNote?: string;
  fisNo?: string;
  tarih: string;
}): SalePrintPayload {
  const { animal } = params;
  return {
    tarih: params.tarih,
    fisNo: params.fisNo,
    kupeNo: getDisplayKupeNo(animal),
    siparisNo: getDisplaySiparisNo(animal),
    customerName: params.customerName,
    saleType: getAnimalSalesType(animal),
    animalType: animal.tur === "Büyükbaş" ? "B.Baş" : "K.Baş",
    hisseLabel: params.hisseLabel,
    saleAmount: params.saleAmount,
    kesimUcreti: params.kesimUcreti,
    parcalamaUcreti: params.parcalamaUcreti,
    tasimaUcreti: params.tasimaUcreti,
    extraServicesTotal: params.kesimUcreti + params.parcalamaUcreti + params.tasimaUcreti,
    grandTotal: params.grandTotal,
    depositAmount: params.depositAmount ?? 0,
    remainingAmount: params.remainingAmount ?? Math.max(0, params.grandTotal - (params.depositAmount ?? 0)),
    operationalNote: params.operationalNote,
  };
}


function renderSimpleTable(headers: string[], rows: string[][]) {
  if (rows.length === 0) {
    return `<div class="summary-row"><span>Kayıt</span><strong>Yok</strong></div>`;
  }

  return `
    <table class="print-table">
      <thead><tr>${headers.map((item) => `<th>${escapeHtml(item)}</th>`).join("")}</tr></thead>
      <tbody>
        ${rows
          .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
          .join("")}
      </tbody>
    </table>`;
}

export function openAnimalSummaryPrintWindow(
  animal: AnimalItem,
  settings = getReportPrintSettings()
) {
  const soldShares = (animal.hisseler ?? []).filter((share) => share.musteriAdi?.trim());
  const firstSaleDate = soldShares.find((share) => share.satisTarihi)?.satisTarihi;

  const rows = soldShares.map((share, index) => [
    `${index + 1}. Hisse`,
    share.musteriAdi || "—",
    share.telefon || "—",
    share.kalan <= 0 ? "Ödeme tamamlandı" : "Ödeme tamamlanmadı",
  ]);

  const body = `
  <div class="page">
    <div class="card">
      <div class="header">
        <div class="brand">Evren Çiftliği</div>
        <div class="title">Hayvan Hisse Çıktısı</div>
        <div class="subtitle">Yarım A4 operasyon özeti</div>
      </div>
      <div class="content">
        <div class="grid">
          ${renderInfo("Küpe No", getDisplayKupeNo(animal))}
          ${renderInfo("Sipariş No", getDisplaySiparisNo(animal))}
          ${renderInfo("Satış Tarihi", fmtDate(firstSaleDate))}
          ${renderInfo("Kesim Günü", fmtDate(animal.kesimGunu))}
          ${renderInfo("Tür", animal.tur)}
          ${renderInfo("Satış Tipi", getAnimalSalesType(animal))}
        </div>
        <div class="summary">
          <div class="summary-head">Hisse Detayı</div>
          ${renderSimpleTable(["Hisse", "Müşteri", "Telefon", "Ödeme"], rows)}
        </div>
      </div>
    </div>
  </div>`;

  return openPrintWindow("Hayvan Hisse Çıktısı", "Yarım A4", body);
}

export function openCustomerSummaryPrintWindow(
  customer: CustomerItem,
  animals: AnimalItem[],
  payments: PaymentRecord[],
  settings = getReportPrintSettings()
) {
  const shareRows: string[][] = [];
  const shareIds = new Set<number>();

  for (const animal of animals) {
    for (const share of animal.hisseler ?? []) {
      if (!isShareOwnedByCustomer(share, customer)) continue;
      shareIds.add(share.id);
      shareRows.push([
        animal.tur === "Büyükbaş" ? "B.Baş" : "K.Baş",
        getDisplayKupeNo(animal),
        getDisplaySiparisNo(animal),
        share.hisseNo,
        fmtMoney(getShareTotal(share)),
        fmtMoney(share.odenen),
        fmtMoney(share.kalan),
      ]);
    }
  }

  const animalById = new Map(animals.map((animal) => [Number(animal.id), animal]));

  const paymentRows = payments
    .filter(
      (payment) =>
        payment.musteriId === customer.id ||
        shareIds.has(payment.shareId) ||
        String(payment.telefon || "").replace(/\D/g, "") === String(customer.telefon || "").replace(/\D/g, "")
    )
    .map((payment) => {
      const animal = animalById.get(Number(payment.animalId));
      return [
        fmtDate(payment.tarih),
        payment.fisNo || payment.havaleNo || "—",
        animal ? (animal.tur === "Büyükbaş" ? "B.Baş" : "K.Baş") : payment.animalId === 0 ? "Hisse Ön Satış" : "—",
        animal ? getDisplayKupeNo(animal) : payment.hayvanKupeNo || "—",
        animal ? getDisplaySiparisNo(animal) : payment.hayvanSiparisNo || "—",
        payment.hisseNo || "—",
        payment.tip,
        fmtMoney(payment.tutar),
      ];
    });

  const body = `
  <div class="page">
    <div class="card">
      <div class="header">
        <div class="brand">Evren Çiftliği</div>
        <div class="title">Müşteri Satış / Ödeme Dökümü</div>
        <div class="subtitle">Yarım A4 müşteri özeti</div>
      </div>
      <div class="content">
        <div class="grid">
          ${renderInfo("Müşteri", customer.adSoyad)}
          ${renderInfo("Telefon", customer.telefon)}
          ${renderInfo("Toplam Borç", fmtMoney(customer.toplamBorc), true)}
          ${renderInfo("Toplam Ödenen", fmtMoney(customer.toplamOdenen), true)}
        </div>
        <div class="summary">
          <div class="summary-head">Satış Kayıtları</div>
          ${renderSimpleTable(["Tür", "Küpe", "Sipariş", "Hisse", "Toplam", "Ödenen", "Kalan"], shareRows)}
        </div>
        <div class="summary">
          <div class="summary-head">Ödeme Dökümü</div>
          ${renderSimpleTable(["Tarih", "Fiş/Dekont", "Tür", "Küpe", "Sipariş", "Hisse", "Tip", "Tutar"], paymentRows)}
        </div>
      </div>
    </div>
  </div>`;

  return openPrintWindow("Müşteri Dökümü", "Yarım A4", body);
}
